import dayjs from 'dayjs';
import moment from 'moment';
import { PropTypes } from 'prop-types';
import React, { forwardRef, useContext, useState, useEffect } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import styled from 'styled-components';

import { DatePicker } from '@ge/components/datepicker';
import { Icon, Icons } from '@ge/components/icon';
import { Input } from '@ge/components/input';
import { DateTimeFormats, TemplateDateValue, DayJs } from '@ge/models/constants';
import { TaskContext } from '@ge/shared/components/tasks/task-context';
import { addUTCOffset, getDateObject, getLocalDateFormat } from '@ge/shared/util/time-date';
import { StatusColor } from '@ge/tokens/colors/colors';

const StyledInput = styled(Input)`
  ${({ errors, fieldArray, id }) => {
    const { fieldName, errorIndex, errorName } = fieldArray ?? {};
    return errors[id] ||
      errors.scheduleDate?.type === id ||
      errors[fieldName]?.[errorIndex]?.[errorName]
      ? `border-color: ${StatusColor.DANGER}`
      : '';
  }}
  &:disabled {
    cursor: not-allowed;
  }
`;

const StyledIcon = styled(Icon).attrs((props) => ({
  color: props.theme.manage.taskItem.iconColor,
}))`
  margin-right: 4px;
`;

const StyledIcon2 = styled(Icon).attrs((props) => ({
  color: props.theme.manage.taskItem.iconColor,
}))`
  margin-right: 19px;
`;

// eslint-disable-next-line react/display-name
const DatePickerIcon = forwardRef(({ onClick }, refDate) => (
  <button type="button" tabIndex="-1" className="task-dialog-date" onClick={onClick} ref={refDate}>
    <StyledIcon icon={Icons.CALENDAR} size={10} />
  </button>
));

const ClearIcon = forwardRef(({ onClick }, refDate) => (
  <button type="button" tabIndex="-1" className="task-dialog-date" onClick={onClick} ref={refDate}>
    <StyledIcon2 icon={Icons.CLOSE} size={8} />
  </button>
));

const StyledErrorMessage = styled.span`
  text-indent: -1.5em;
  color: ${StatusColor.DANGER};
  margin-top: 5px;

  &:before {
    content: '\\2296'; // &ominus;
    font-size: 13px;
    padding-right: 2px;
  }
`;

DatePickerIcon.propTypes = {
  onClick: PropTypes.func,
};

DatePickerIcon.propTypes = {
  onClick: () => {},
};

ClearIcon.propTypes = {
  onClick: PropTypes.func,
};

ClearIcon.propTypes = {
  onClick: () => {},
};

export const DateMetaField = ({
  defaultSelection,
  defaultValue,
  fieldArray,
  metadata,
  name,
  rules,
  validators,
  min,
  max,
}) => {
  const { readOnly, required, isInput, isCustomErr } = metadata;
  const { control, errors, setValue, getValues, setError, clearErrors } = useFormContext();
  const [startDateNew, setStartDate] = useState('');
  const [startDateFormated, setformatedStartDate] = useState(null);
  const [customError, setCustomError] = useState(false);
  const { placeholder, format, literal } = getLocalDateFormat();
  const {
    taskState: { timezone },
  } = useContext(TaskContext);

  const formatDate = (date) =>
    date
      ? dayjs(date).format(
          isInput ? DateTimeFormats.DATEPICKER_CUSTOM_DATE : DateTimeFormats.NEW_TASK,
        )
      : null;
  const earliestDate = validators?.map((validator) => validator.earliestDate)[0] || '';
  const latestDate = validators?.map((validator) => validator.latestDate)[0] || '';
  const minDate =
    earliestDate === TemplateDateValue.CURRENT_DATE
      ? getDateObject(addUTCOffset(dayjs(), timezone))
      : min
      ? new Date(min)
      : null;
  const maxDate =
    latestDate === TemplateDateValue.CURRENT_DATE
      ? getDateObject(addUTCOffset(dayjs(), timezone))
      : max
      ? new Date(max)
      : null;
  const startDate = dayjs(defaultValue).isValid()
    ? getDateObject(dayjs(defaultValue))
    : defaultSelection === TemplateDateValue.CURRENT_DATE
    ? getDateObject(addUTCOffset(dayjs(), timezone))
    : null;
  if (required) rules = { ...rules, required: true };

  useEffect(() => {
    if (dayjs(getValues(name)).isValid() && !startDateNew && !startDateFormated) {
      let date = getValues(name);
      setValue(name, date);
      setformatedStartDate(formatDate(date));
    }
  });

  const handleChangeRaw = (value) => {
    const re = new RegExp(`^[0-9${literal}]*$`);
    if (!re.test(value)) return;
    let formatNew = placeholder.split(literal);
    let date = value?.split(literal);
    let dayIndex = formatNew.indexOf('DD');
    let monthIndex = formatNew.indexOf('MM');
    let yearIndex = formatNew.indexOf('YYYY');
    if (
      (date?.length === 1 && formatNew[0]?.length === date[0]?.length) ||
      (date?.length === 2 && formatNew[1]?.length === date[1]?.length)
    ) {
      setStartDate(`${value}${literal}`);
    } else if (date?.length === 3) {
      if (yearIndex === 2) {
        if (date[2]?.length <= 4) setStartDate(value);
      } else if (dayIndex === 2 || monthIndex === 2) {
        if (date[2]?.length <= 2) setStartDate(value);
      }
    } else {
      setStartDate(value);
    }
  };

  const handleCalendarClose = () => {
    if (!isInput) return null;
    let dateMomentObject = moment(startDateNew, placeholder); // 1st argument - string, 2nd argument - format
    let dateObject = dateMomentObject.toDate();
    let year = moment(dateObject, 'DD/MM/YYYY').year();
    if (moment(dateMomentObject).isValid() && year >= 2000) {
      setCustomError(false);
      clearErrors(name);
      setValue(name, dateObject);
      setformatedStartDate(formatDate(dateObject));
    } else handleClearDates(true);
  };

  const handleClearDates = (state = true) => {
    if (state && startDateNew) {
      isCustomErr
        ? setCustomError(true)
        : setError(name, { type: 'custom', message: DayJs.INVALID_DATE });
    }
    setformatedStartDate(null);
    setValue(name, null);
    setStartDate('');
  };

  const handleDateChange = (value, e) => {
    if (!e.target.value) setStartDate(value);
  };

  const getChangeValues = (value) => {
    const _value = dayjs(startDateFormated).isValid() ? new Date(startDateFormated) : null;
    return isInput
      ? _value
      : value === TemplateDateValue.CURRENT_DATE
      ? getDateObject(addUTCOffset(dayjs(), timezone))
      : value;
  };

  const getEnteredValue = (value) => {
    let inputVal = startDateFormated || startDateNew;
    return isInput ? inputVal : value ? formatDate(value) : '';
  };

  return (
    <Controller
      control={control}
      name={name}
      defaultValue={startDate}
      rules={rules}
      render={({ onChange, value }) => {
        const _value = getChangeValues(value);
        return (
          <>
            <DatePicker
              customInput={
                <div className="date-picker">
                  <StyledInput
                    autoComplete="off"
                    errors={errors}
                    fieldArray={fieldArray}
                    id={name}
                    onChange={isInput ? (event) => handleChangeRaw(event.target.value) : () => null}
                    value={getEnteredValue(_value)}
                    disabled={readOnly}
                    placeholder={isInput ? placeholder : ''}
                  />
                  <DatePickerIcon />
                  {isInput && startDateFormated ? (
                    <ClearIcon onClick={() => handleClearDates(false)} />
                  ) : null}
                </div>
              }
              disabled={readOnly}
              minDate={minDate}
              maxDate={maxDate}
              selected={_value || null}
              startDate={startDate}
              placeholderText={isInput ? placeholder : ''}
              onChange={isInput ? (date, e) => handleDateChange(date, e) : onChange}
              onCalendarClose={handleCalendarClose}
              {...(isInput && { dateFormat: format })}
            />
            {customError && isInput && isCustomErr && (
              <StyledErrorMessage role="alert">{DayJs.INVALID_DATE}</StyledErrorMessage>
            )}
          </>
        );
      }}
    />
  );
};

DateMetaField.propTypes = {
  defaultSelection: PropTypes.string,
  defaultValue: PropTypes.oneOfType([PropTypes.instanceOf(Date), PropTypes.string, null]),
  fieldArray: PropTypes.instanceOf(Object),
  metadata: PropTypes.instanceOf(Object).isRequired,
  name: PropTypes.string.isRequired,
  rules: PropTypes.instanceOf(Object),
  validators: PropTypes.instanceOf(Array),
  min: PropTypes.string,
  max: PropTypes.string,
};

DateMetaField.defaultProps = {
  defaultSelection: '',
  defaultValue: null,
  fieldArray: null,
  rules: null,
  validators: [],
};
